import cronstrue from 'cronstrue';
import { type TranslateFn } from 'src/arb-wrapper';
import { BOX_FOLDER_ID_REGEX, BOX_FOLDER_URL_BASE, EMPTY_TABLE_VALUE } from 'src/constants';

/**
 * Human-facing Box link for a bare folder id.
 *
 * Note there is deliberately no composite-id construction or parsing here. Bender's
 * `syncTargetId` (`box:<folderId>`) is only ever received from the API and handed
 * straight back in a request path, so the frontend never needs to build or split one.
 */
export const boxFolderUrl = (targetId: string): string => `${BOX_FOLDER_URL_BASE}${targetId}`;

const BOX_FOLDER_URL_PATH = /(?:^|\/)folder\/(\d{1,20})(?:\/|$)/;

/**
 * Canonicalizes user input into a bare numeric Box folder id, accepting either a bare
 * id or a Box folder URL.
 *
 * This deliberately mirrors Bender's `ParseTargetInputPipe` exactly — `https:` only,
 * host `app.box.com` or a subdomain of it, path segment `/folder/<digits>`. Accepting
 * anything looser here means the client would happily submit input the server then
 * rejects with a 400.
 */
export const parseBoxFolderInput = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  if (BOX_FOLDER_ID_REGEX.test(trimmed)) {
    return trimmed;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return undefined;
  }

  if (url.protocol !== 'https:') {
    return undefined;
  }
  if (url.hostname !== 'app.box.com' && !url.hostname.endsWith('.app.box.com')) {
    return undefined;
  }

  return BOX_FOLDER_URL_PATH.exec(url.pathname)?.[1];
};

const RATE_EXPRESSION = /^rate\((\d+) (minute|hour|day)s?\)$/;

const CRON_EXPRESSION = /^cron\((.+)\)$/;

const RATE_UNIT_KEYS: Record<string, { singular: string; plural: string }> = {
  minute: { singular: 'box_folder_schedule_every_minute', plural: 'box_folder_schedule_every_minutes' },
  hour: { singular: 'box_folder_schedule_hourly', plural: 'box_folder_schedule_every_hours' },
  day: { singular: 'box_folder_schedule_daily', plural: 'box_folder_schedule_every_days' },
};

const describeCron = (fields: string): string | undefined => {
  try {
    // EventBridge cron has no seconds field; the leading "0 " makes cronstrue read it as seconds-first.
    return `${cronstrue.toString(`0 ${fields}`, { verbose: true })} (UTC)`;
  } catch {
    return undefined;
  }
};

/**
 * Readable form of a sync target's EventBridge `refreshSchedule`.
 *
 * `rate(...)` and `cron(...)` are described in words; anything cronstrue cannot parse, or any
 * other expression, is shown raw so the cell never hides the real schedule.
 */
export const formatRefreshSchedule = (refreshSchedule: string | undefined, t: TranslateFn): string => {
  const expression = refreshSchedule?.trim();
  if (!expression) {
    return EMPTY_TABLE_VALUE;
  }

  const rate = RATE_EXPRESSION.exec(expression);
  if (rate) {
    const count = Number(rate[1]);
    const keys = RATE_UNIT_KEYS[rate[2]];
    return count === 1 ? t(keys.singular) : t(keys.plural, { count });
  }

  const cron = CRON_EXPRESSION.exec(expression);
  return (cron && describeCron(cron[1])) ?? expression;
};
